import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  onUnregistered,
  register,
  unregister,
  type Messaging,
} from 'firebase/messaging';

import notificationWorkerUrl from '../firebase-messaging-sw.ts?worker&url';
import { app, db } from '../Firebase';
import { firebaseMessagingVapidKey } from '../firebase-config';

const FID_STORAGE_KEY_PREFIX = 'notifications.fid';

export type NotificationCapability =
  'ready' | 'blocked' | 'unsupported' | 'ios-install-required';

interface ActiveRegistration {
  isCleaningUp: boolean;
  messaging: Messaging;
  pendingRegistration: Promise<void> | null;
  unsubscribeListeners: () => void;
  userId: string;
}

interface RegistrationHandlers {
  onRegistered: () => void;
  onRegistrationError: (error: unknown) => void;
}

let activeRegistration: ActiveRegistration | null = null;
let registrationOperation = Promise.resolve();

const enqueueRegistrationOperation = async <T>(operation: () => Promise<T>) => {
  const result = registrationOperation.then(operation, operation);
  registrationOperation = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
};

const getFidStorageKey = (userId: string) =>
  `${FID_STORAGE_KEY_PREFIX}:${userId}`;

const getStoredFid = (userId: string): string | null => {
  try {
    return window.localStorage.getItem(getFidStorageKey(userId));
  } catch (error) {
    console.error('[Notifications] Failed to read the stored FID', error);
    return null;
  }
};

const setStoredFid = (userId: string, fid: string | null) => {
  try {
    if (fid) {
      window.localStorage.setItem(getFidStorageKey(userId), fid);
      return;
    }

    window.localStorage.removeItem(getFidStorageKey(userId));
  } catch (error) {
    console.error('[Notifications] Failed to persist the FID', error);
  }
};

const isIosDevice = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

const isStandalone = () => {
  const navigatorWithStandalone = navigator as Navigator & {
    standalone?: boolean;
  };

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  );
};

export const getNotificationCapability =
  async (): Promise<NotificationCapability> => {
    if (isIosDevice() && !isStandalone()) {
      return 'ios-install-required';
    }

    if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !(await isSupported())
    ) {
      return 'unsupported';
    }

    return Notification.permission === 'denied' ? 'blocked' : 'ready';
  };

const deleteDevice = async (userId: string, fid: string) => {
  try {
    await deleteDoc(doc(db, 'notificationDevices', fid));
  } catch (error) {
    console.error('[Notifications] Failed to delete device registration', {
      userId,
      fid,
      error,
    });
  }
};

const stopRegistrationListeners = (registration: ActiveRegistration) => {
  registration.unsubscribeListeners();
  if (activeRegistration === registration) {
    activeRegistration = null;
  }
};

const deactivateRegistration = async (registration: ActiveRegistration) => {
  registration.isCleaningUp = true;
  stopRegistrationListeners(registration);
  await registration.pendingRegistration;
};

const waitForServiceWorkerActivation = (worker: ServiceWorker) => {
  if (worker.state === 'activated') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const handleStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', handleStateChange);
        resolve();
      }

      if (worker.state === 'redundant') {
        worker.removeEventListener('statechange', handleStateChange);
        reject(new Error('Firebase Messaging service worker became redundant'));
      }
    };

    worker.addEventListener('statechange', handleStateChange);
  });
};

const registerNotificationServiceWorker = async () => {
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    notificationWorkerUrl,
    {
      scope: '/',
      type: import.meta.env.DEV ? 'module' : 'classic',
    },
  );
  const pendingWorker =
    serviceWorkerRegistration.installing ?? serviceWorkerRegistration.waiting;
  if (pendingWorker) {
    await waitForServiceWorkerActivation(pendingWorker);
  }

  return serviceWorkerRegistration;
};

// `register` resolves from its cached installation state, so a browser that
// silently lost its push subscription (storage eviction, cleared site data, a
// permission reset) would be reported as registered while every send fails with
// `messaging/installation-id-not-registered`. Dropping the stale installation
// first forces a real re-subscribe.
const discardStaleRegistration = async (
  messaging: Messaging,
  serviceWorkerRegistration: ServiceWorkerRegistration,
) => {
  const pushSubscription =
    await serviceWorkerRegistration.pushManager.getSubscription();
  if (pushSubscription) {
    return;
  }

  try {
    await unregister(messaging);
  } catch (error) {
    console.error(
      '[Notifications] Failed to discard a stale registration',
      error,
    );
  }
};

const startRegistration = async (
  userId: string,
  handlers: RegistrationHandlers,
) => {
  if (!firebaseMessagingVapidKey) {
    throw new Error('Firebase Messaging VAPID key is not configured');
  }

  if (Notification.permission !== 'granted') {
    throw new Error('Browser notification permission has not been granted');
  }

  if (activeRegistration) {
    await deactivateRegistration(activeRegistration);
  }

  const serviceWorkerRegistration = await registerNotificationServiceWorker();
  const messaging = getMessaging(app);
  await discardStaleRegistration(messaging, serviceWorkerRegistration);

  const registration: ActiveRegistration = {
    isCleaningUp: false,
    messaging,
    pendingRegistration: null,
    userId,
    unsubscribeListeners: () => undefined,
  };
  const isActive = () =>
    activeRegistration === registration && !registration.isCleaningUp;

  const unsubscribeRegistered = onRegistered(messaging, (fid) => {
    if (!isActive()) {
      return;
    }

    registration.pendingRegistration = setDoc(
      doc(db, 'notificationDevices', fid),
      {
        fid,
        userId,
        registeredAt: serverTimestamp(),
      },
    )
      .then(async () => {
        if (!isActive()) {
          await deleteDevice(userId, fid);
          return;
        }

        setStoredFid(userId, fid);
        handlers.onRegistered();
      })
      .catch((error) => {
        if (isActive()) {
          handlers.onRegistrationError(error);
        }
      });
  });
  const unsubscribeUnregistered = onUnregistered(messaging, (fid) => {
    setStoredFid(userId, null);
    if (!registration.isCleaningUp) {
      void deleteDevice(userId, fid);
    }
  });
  const unsubscribeMessage = onMessage(messaging, () => undefined);

  registration.unsubscribeListeners = () => {
    unsubscribeRegistered();
    unsubscribeUnregistered();
    unsubscribeMessage();
  };
  activeRegistration = registration;

  try {
    await register(messaging, {
      serviceWorkerRegistration,
      vapidKey: firebaseMessagingVapidKey,
    });
  } catch (error) {
    await deactivateRegistration(registration);
    throw error;
  }
};

export const startNotificationRegistration = (
  userId: string,
  handlers: RegistrationHandlers,
) => enqueueRegistrationOperation(() => startRegistration(userId, handlers));

const cleanupRegistration = async (userId: string) => {
  const registration =
    activeRegistration?.userId === userId ? activeRegistration : null;
  if (registration) {
    await deactivateRegistration(registration);
  }
  const fid = getStoredFid(userId);
  if (fid) {
    await deleteDevice(userId, fid);
    setStoredFid(userId, null);
  }

  try {
    const messaging = registration
      ? registration.messaging
      : (await isSupported())
        ? getMessaging(app)
        : null;

    if (!messaging) {
      return;
    }

    await unregister(messaging);
  } catch (error) {
    console.error('[Notifications] Failed to unregister this browser', {
      userId,
      error,
    });
  }
};

export const cleanupNotificationRegistration = (userId: string) =>
  enqueueRegistrationOperation(() => cleanupRegistration(userId));
